<script>
  import { writable } from 'svelte/store';
  import { onMount, onDestroy } from 'svelte';

  onMount(() => {
    // No additional setup required here for this case
  });

  onDestroy(() => {
    // Ensure scrolling is re-enabled if the component is destroyed while the lightbox is open
    document.body.classList.remove('no-scroll');
  });


  export let imageUrl;
  export let imageWidth;
  export let imageHeight;
  export let title;
  export let altText;

  let isLightboxOpen = false;
  let adjustedImageWidth, adjustedImageHeight; // Declare reactive variables

  function openLightbox() {
    isLightboxOpen = true;
    document.body.classList.add('no-scroll');
  }

  function closeLightbox() {
    isLightboxOpen = false;
    document.body.classList.remove('no-scroll');
  }

  // Function to determine the unit of the passed dimension
  function getUnit(value) {
    if (typeof value === 'string') {
      if (value.endsWith('vh')) return 'vh';
      if (value.endsWith('vw')) return 'vw';
      if (value.endsWith('%')) return '%';
      if (value.endsWith('px')) return 'px';
    }
    return null; // No recognized unit found
  }

  // Function to adjust dimensions based on the unit
  function adjustDimension(value, unit) {
    if (unit === 'auto') return 'auto';

    const numericalValue = parseFloat(value); // Convert to float for operations
    if (isNaN(numericalValue)) return value; // Return original value if not a number

    // Adjustments for narrow screens
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      switch (unit) {
        case 'vh':
        case 'vw':
        case '%':
          // For viewport and percentage units, consider reducing the size slightly for better mobile view
          return `${numericalValue * 0.9}${unit}`;
        case 'px':
          // Halve pixel values on narrow screens
          return `${numericalValue / 1.5}px`;
        default:
          return value; // No adjustment if unit is unrecognized
      }
    }

    // Return the original value for wider screens or if no adjustment is needed
    return value;
  }

  // Reactive statements for image dimensions with unit handling
  $: adjustedImageWidth = adjustDimension(imageWidth, getUnit(imageWidth));
  $: adjustedImageHeight = adjustDimension(imageHeight, getUnit(imageHeight));
</script>

<img 
  src={imageUrl} 
  width={adjustedImageWidth} 
  height={adjustedImageHeight} 
  alt={altText ? altText : 'Image'}
  class="clickable-image" 
  on:click={openLightbox} 
/>

{#if isLightboxOpen}
  <div class="lightbox" on:click={closeLightbox}>
    <div class="lightbox-content">
      <img src={imageUrl} alt={altText ? altText : 'Image'} class="lightbox-image" />
      {#if title}
        <h1 class="image-title">{title}</h1>
      {/if}
      {#if altText}
        <h2 class="image-alt-text">{altText}</h2>
      {/if}
    </div>
    <button class="close-button" on:click|stopPropagation={closeLightbox}>✕</button>
  </div>
{/if}

<style>
  .no-scroll {
    overflow: hidden;
  }
  .clickable-image {
    cursor: pointer;
  }

  .lightbox {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000; /* High z-index to ensure it's on top of everything else */
  }

  .lightbox-image {
    max-width: 90%;
    max-height: 90%;
    object-fit: contain;
  }

  .close-button {
    position: fixed;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    color: white;
    font-size: 24px;
    cursor: pointer;
    z-index: 1001; /* Even higher z-index to be on top of the lightbox */
  }

  img {
  margin: 0;
  padding: 0;
  object-fit: cover;
  }

  .lightbox-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  .lightbox-content img {
    max-height: 77vh;
  }

  .image-title, .image-alt-text {
    color: white;}

  h1 {
    font-size: 3.4rem;
    font-weight: normal;
  }

  h2 {
    font-size: 1rem;
    font-weight: normal;
  }

  /* CSS Media Queries for Text Size */
  @media (max-width: 768px) {
    h1 {
      font-size: 1.7rem; /* Half the size */
    }

    h2 {
      font-size: 0.5rem; /* Half the size */
    }
  }
</style>